import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewRole, setPreviewRole] = useState(null); // 'neighbor' | 'volunteer' | null

  const checkAuth = useCallback(async () => {
    try {
      const data = await authApi.getMe();
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = () => { window.location.href = '/api/auth/google'; };

  const logout = async () => {
    try { await authApi.logout(); } finally { setUser(null); setPreviewRole(null); }
  };

  // Effective role considers preview mode
  const effectiveRole = previewRole || user?.role;
  const isVolunteer = effectiveRole && ['volunteer', 'admin'].includes(effectiveRole);
  const isAdmin = !previewRole && user?.role === 'admin'; // Admin UI only when NOT previewing
  const isChair = user?.is_committee_chair === true;
  const isRealAdmin = user?.role === 'admin'; // Always true for real admins regardless of preview
  const isPreviewing = previewRole !== null;

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, checkAuth,
      isVolunteer, isAdmin, isChair, isRealAdmin, isPreviewing,
      effectiveRole, previewRole, setPreviewRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
